import { apiClient } from './apiClient';

export interface Waypoint {
  id: number;
  code: string;
  waypoint_date: string;
  auditor: {
    id: number;
    name: string;
  };
  planning?: {
    id: number;
    name: string;
  };
  waypoints_count: number;
  gpx_file_url: string | null;
  created: string;
}

export interface WaypointDetail extends Waypoint {
  waypoints: Array<{
    id: number;
    name: string;
    latitude: number;
    longitude: number;
    date_taken: string | null;
  }>;
}

export interface UploadWaypointParams {
  gpx_file: File;
  auditor_id: number;
  waypoint_date: string;
}

export const waypointService = {
  /**
   * Upload waypoints GPX file for a planning group
   */
  uploadWaypoints: async (planningId: number, params: UploadWaypointParams) => {
    const formData = new FormData();
    formData.append('gpx_file', params.gpx_file);
    formData.append('auditor_id', params.auditor_id.toString());
    formData.append('waypoint_date', params.waypoint_date);

    // Override the apiClient's default `Content-Type: application/json`
    // for this request. Setting it to `undefined` makes axios re-detect
    // the body type and emit `multipart/form-data; boundary=...` with
    // the proper boundary parameter. Without this, the inherited JSON
    // default sticks, the file part of the body never reaches Django,
    // and the endpoint returns 400 "GPX file is required".
    const response = await apiClient.post(
      `/planning-groups/${planningId}/waypoints/upload/`,
      formData,
      { headers: { 'Content-Type': undefined } as any },
    );
    return response.data;
  },

  /**
   * List waypoints for a planning group
   */
  listWaypoints: async (planningId: number, auditorId?: number): Promise<Waypoint[]> => {
    const params = auditorId ? { auditor_id: auditorId } : {};
    const response = await apiClient.get(`/planning-groups/${planningId}/waypoints/`, { params });
    return response.data;
  },

  /**
   * Get waypoint detail with all geometries
   */
  getWaypoint: async (waypointId: number): Promise<WaypointDetail> => {
    const response = await apiClient.get(`/waypoints/${waypointId}/`);
    return response.data;
  },

  /**
   * Delete waypoint file
   */
  deleteWaypoint: async (waypointId: number) => {
    const response = await apiClient.delete(`/waypoints/${waypointId}/`);
    return response.data;
  },
};
